import Seo from "../seo/Seo";
import { postMetadata } from "../seo/metadata";
import { articleSchema } from "../seo/schema";
import NotFound from "./NotFound";
import FooterSection from "../components/sections/FooterSection";
import { useParams, Link } from 'react-router-dom';

import { blogPosts } from '../data/blogPosts';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Navbar from '../components/Navbar';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '../constants/urls';

export default function BlogPost() {
    const { slug } = useParams();
    const post = blogPosts.find((p) => p.slug === slug);

    if (!post) {
        return <NotFound />;
    }

    const metadata = postMetadata(post);
    const relatedPosts = blogPosts.filter((candidate) => candidate.slug !== post.slug && candidate.category === post.category).slice(0, 3);

    return (
        <>
            <Navbar />
        <main id="main-content" className="min-h-screen" style={{ background: 'var(--color-night)', color: 'var(--color-on-night)' }} aria-labelledby="post-heading">
            <div className="blog-post-page pt-28 md:pt-36 pb-24 md:pb-32">
                <Seo metadata={metadata} structuredData={articleSchema(post, metadata)} />

                <article className="container mx-auto px-4 sm:px-6 max-w-3xl">
                    <Link
                        to="/blog"
                        className="blog-back inline-flex items-center mb-10 group font-display font-bold uppercase"
                        style={{ fontSize: 'var(--text-sm)', letterSpacing: 'var(--tracking-label)' }}
                    >
                        <ArrowLeftIcon className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                        Back to Blog
                    </Link>

                    <header className="blog-post-header mb-12 md:mb-16">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="font-display font-bold uppercase" style={{ color: 'var(--color-accent)', fontSize: 'var(--text-xs)', letterSpacing: 'var(--tracking-label)' }}>
                                {post.category}
                            </span>
                            <span aria-hidden="true" style={{ color: 'var(--color-night-rule)' }}>•</span>
                            <time dateTime={post.date} style={{ color: 'var(--color-on-night-muted)', fontSize: 'var(--text-sm)' }}>{post.date}</time>
                        </div>

                        <h1 id="post-heading" className="font-display font-bold uppercase mb-6" style={{ fontSize: 'var(--text-display)', lineHeight: 'var(--lh-tight)' }}>
                            {post.title}
                        </h1>

                        <p className="text-lg md:text-xl max-w-2xl" style={{ color: 'var(--color-on-night-muted)', lineHeight: 'var(--lh-body)' }}>
                            {post.excerpt}
                        </p>
                    </header>

                    {post.image && (
                        <figure className="mb-12 md:mb-16">
                            <div
                                className="overflow-hidden flex items-center justify-center"
                                style={{
                                    background: 'var(--color-night-2)',
                                    border: 'var(--rule-fine) solid var(--color-night-rule)'
                                }}
                            >
                                <img
                                    src={post.image}
                                    width={post.imageWidth}
                                    height={post.imageHeight}
                                    decoding="async"
                                    fetchpriority="high"
                                    alt={post.imageAlt}
                                    className="block max-w-full h-auto"
                                    style={{
                                        width: 'auto',
                                        maxHeight: 'min(70vh, 42rem)',
                                        objectFit: 'contain',
                                        objectPosition: 'center'
                                    }}
                                />
                            </div>
                            <figcaption className="mt-3 text-sm" style={{ color: 'var(--color-on-night-muted)', lineHeight: 'var(--lh-body)' }}>
                                {post.imageCaption}{' '}
                                Image:{' '}
                                {post.imageCreditUrl ? (
                                    <a href={post.imageCreditUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent)', textDecoration: 'underline', textUnderlineOffset: '0.2em' }}>{post.imageCredit}</a>
                                ) : (
                                    <span>{post.imageCredit}</span>
                                )}
                                {post.imageLicense && (
                                    <>
                                        {' '}(
                                        {post.imageLicenseUrl ? (
                                            <a href={post.imageLicenseUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent)', textDecoration: 'underline', textUnderlineOffset: '0.2em' }}>{post.imageLicense}</a>
                                        ) : post.imageLicense}
                                        ).
                                    </>
                                )}
                            </figcaption>
                        </figure>
                    )}

                    <div className="blog-prose pt-10" style={{ borderTop: 'var(--rule-fine) solid var(--color-night-rule)' }}>
                        <div
                            dangerouslySetInnerHTML={{ __html: post.content }}
                        />
                    </div>

                    {relatedPosts.length > 0 && (
                        <nav aria-label="Related articles" className="mt-12 blog-prose">
                            <h2>More {post.category.toLowerCase()} guides and stories</h2>
                            <ul>{relatedPosts.map((related) => <li key={related.slug}><Link to={`/blog/${related.slug}`}>{related.title}</Link></li>)}</ul>
                        </nav>
                    )}
                    <div className="blog-cta mt-16 p-7 md:p-10" style={{ border: 'var(--rule-fine) solid var(--color-night-rule)', background: 'var(--color-night-2)' }}>
                        <p className="font-display font-bold uppercase mb-3" style={{ color: 'var(--color-accent)', fontSize: 'var(--text-sm)', letterSpacing: 'var(--tracking-label)' }}>Your next match</p>
                        <h2 className="font-display font-bold uppercase mb-4" style={{ fontSize: 'var(--text-2xl)', lineHeight: 'var(--lh-snug)' }}>Ready to improve your game?</h2>
						<p className="mb-6" style={{ color: 'var(--color-on-night-muted)' }}>
							Download Racketly for iOS, Apple Watch or Android and start tracking your matches today.
						</p>
						<div className="flex flex-col sm:flex-row gap-3">
							<a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn-slab btn-slab--accent lift">Download on App Store</a>
							<a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="btn-slab btn-slab--outline-night lift">Get it on Google Play</a>
						</div>
                        <p className="mt-6 blog-prose"><Link to="/download">Choose your Racketly download</Link> or <Link to="/blog/how-to-use-racketly">read the match tracking guide</Link>.</p>
                    </div>
                </article>
            </div>
        </main>
            <FooterSection isDark={true} />
        </>
    );
}
